import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Save, RotateCcw, Utensils, ArrowUp, ArrowDown, Trash2, Plus,
  Undo2, Eye, EyeOff, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));
const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * AdminMenuManager
 *
 * Full CRUD + reorder + visibility over the couple's wedding-wide menu.
 * The Admin builds a tree of Sections → Categories → Items, each with
 * its own hidden toggle. `visible` at the root controls whether the
 * public /menu page renders at all.
 */
const AdminMenuManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [openSections, setOpenSections] = useState({});
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/menu/content`, { headers: authHeader(), params: { _: Date.now() } });
      setInitial(clone(r.data));
      setDoc(clone(r.data));
      // Default every section open on first load
      const open = {};
      (r.data.sections || []).forEach((s) => { open[s.id] = true; });
      setOpenSections(open);
    } catch {
      setError('Could not load the wedding menu.');
    } finally { setLoading(false); }
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => initial && JSON.stringify(doc) !== JSON.stringify(initial), [doc, initial]);

  const setSections = (mut) => setDoc((d) => ({ ...d, sections: mut(d.sections || []) }));
  const moveArr = (arr, i, dir) => {
    const j = i + dir; if (j < 0 || j >= arr.length) return arr;
    const next = [...arr]; [next[i], next[j]] = [next[j], next[i]]; return next;
  };

  // ── Section operations ──────────────────────────────────────
  const addSection = () => setSections((s) => [...s, {
    id: uid(), name: 'New Section', style: 'Buffet Style', hidden: false, categories: [],
  }]);
  const updateSection = (si, patch) => setSections((s) => s.map((x, i) => i === si ? { ...x, ...patch } : x));
  const removeSection = (si) => setSections((s) => s.filter((_, i) => i !== si));
  const moveSection = (si, dir) => setSections((s) => moveArr(s, si, dir));

  // ── Category operations ─────────────────────────────────────
  const addCategory = (si) => updateSection(si, {
    categories: [...(doc.sections[si].categories || []), { id: uid(), name: 'New Category', hidden: false, items: [] }],
  });
  const updateCategory = (si, ci, patch) => updateSection(si, {
    categories: doc.sections[si].categories.map((c, i) => i === ci ? { ...c, ...patch } : c),
  });
  const removeCategory = (si, ci) => updateSection(si, {
    categories: doc.sections[si].categories.filter((_, i) => i !== ci),
  });
  const moveCategory = (si, ci, dir) => updateSection(si, {
    categories: moveArr(doc.sections[si].categories, ci, dir),
  });

  // ── Item operations ─────────────────────────────────────────
  const updateItems = (si, ci, mut) => updateCategory(si, ci, {
    items: mut(doc.sections[si].categories[ci].items || []),
  });
  const addItem = (si, ci) => updateItems(si, ci, (a) => [...a, { id: uid(), name: '', hidden: false }]);
  const updateItem = (si, ci, ii, patch) => updateItems(si, ci, (a) => a.map((x, i) => i === ii ? { ...x, ...patch } : x));
  const removeItem = (si, ci, ii) => updateItems(si, ci, (a) => a.filter((_, i) => i !== ii));
  const moveItem = (si, ci, ii, dir) => updateItems(si, ci, (a) => moveArr(a, ii, dir));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await axios.put(`${API}/admin/menu/content`, doc, { headers: authHeader() });
      setInitial(clone(r.data)); setDoc(clone(r.data));
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };
  const revert = () => { setDoc(clone(initial)); setError(''); };
  const resetDefaults = async () => {
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/menu/content/reset`, {}, { headers: authHeader() });
      setInitial(clone(r.data)); setDoc(clone(r.data)); setResetOpen(false);
    } catch { setError('Failed to reset.'); }
    finally { setResetting(false); }
  };

  if (loading || !doc) {
    return (
      <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading wedding menu…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-menu-manager">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-[#F8F5EF] text-[#C8A951] inline-flex items-center justify-center">
            <Utensils className="w-4 h-4" />
          </span>
          <div>
            <h2 className="font-serif text-xl text-[#3B2F2F]">Wedding Menu · Catering</h2>
            <p className="text-xs text-[#8A7A7A]">Edits go live on gracemadeus.com/menu the moment you save — no redeploy needed.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-xs text-[#3B2F2F]" data-testid="menu-visible-toggle">
            <Switch checked={!!doc.visible} onCheckedChange={(v) => setDoc((d) => ({ ...d, visible: !!v }))} />
            <span>{doc.visible ? 'Menu is public' : 'Menu is hidden'}</span>
          </label>
          <button onClick={() => setResetOpen(true)}
            className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1"
            data-testid="menu-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={revert} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="menu-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving}
            className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60"
            data-testid="menu-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {error && <p className="text-red-500 text-xs" data-testid="menu-error">{error}</p>}

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <label className="text-[11px] uppercase tracking-widest text-[#8A7A7A] mb-2 block">Public tagline</label>
        <Input
          value={doc.tagline || ''}
          onChange={(e) => setDoc((d) => ({ ...d, tagline: e.target.value }))}
          placeholder="A thoughtfully prepared selection for our celebration."
          data-testid="menu-tagline-input"
        />
      </div>

      {doc.sections.map((section, si) => {
        const open = openSections[section.id] ?? true;
        return (
          <div key={section.id} className="bg-white rounded-xl shadow-sm p-4 md:p-5" data-testid={`menu-section-${si}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setOpenSections((o) => ({ ...o, [section.id]: !open }))}
                className="p-1 rounded hover:bg-[#F8F5EF]" data-testid={`menu-section-toggle-${si}`}>
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <Input value={section.name}
                onChange={(e) => updateSection(si, { name: e.target.value })}
                className="max-w-xs font-serif text-lg" data-testid={`menu-section-name-${si}`} />
              <Input value={section.style || ''} placeholder="e.g. Buffet Style"
                onChange={(e) => updateSection(si, { style: e.target.value })}
                className="max-w-xs text-xs" data-testid={`menu-section-style-${si}`} />
              <div className="flex-1" />
              <button onClick={() => updateSection(si, { hidden: !section.hidden })}
                className={`p-1.5 rounded ${section.hidden ? 'text-[#8A7A7A] bg-[#F8F5EF]' : 'text-[#3B2F2F]'} hover:bg-[#F8F5EF]`}
                title={section.hidden ? 'Hidden from public menu — click to show' : 'Visible on public menu — click to hide'}
                data-testid={`menu-section-hide-${si}`}>
                {section.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => moveSection(si, -1)} disabled={si === 0}
                className="p-1.5 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-section-up-${si}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => moveSection(si, 1)} disabled={si === doc.sections.length - 1}
                className="p-1.5 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-section-down-${si}`}><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => removeSection(si)}
                className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`menu-section-delete-${si}`}><Trash2 className="w-4 h-4" /></button>
            </div>

            {open && (
              <div className="mt-3 pl-6 space-y-3">
                {section.categories.map((cat, ci) => (
                  <div key={cat.id} className="border border-[#E5E0D8] rounded-lg p-3" data-testid={`menu-category-${si}-${ci}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Input value={cat.name}
                        onChange={(e) => updateCategory(si, ci, { name: e.target.value })}
                        className="max-w-xs font-medium" data-testid={`menu-category-name-${si}-${ci}`} />
                      <div className="flex-1" />
                      <button onClick={() => updateCategory(si, ci, { hidden: !cat.hidden })}
                        className={`p-1.5 rounded ${cat.hidden ? 'text-[#8A7A7A] bg-[#F8F5EF]' : 'text-[#3B2F2F]'} hover:bg-[#F8F5EF]`}
                        title={cat.hidden ? 'Hidden — click to show' : 'Visible — click to hide'}
                        data-testid={`menu-category-hide-${si}-${ci}`}>
                        {cat.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button onClick={() => moveCategory(si, ci, -1)} disabled={ci === 0}
                        className="p-1.5 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-category-up-${si}-${ci}`}><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => moveCategory(si, ci, 1)} disabled={ci === section.categories.length - 1}
                        className="p-1.5 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-category-down-${si}-${ci}`}><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => removeCategory(si, ci)}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`menu-category-delete-${si}-${ci}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <ul className="space-y-1.5">
                      {(cat.items || []).map((item, ii) => (
                        <li key={item.id} className="flex items-center gap-2" data-testid={`menu-item-${si}-${ci}-${ii}`}>
                          <span className="w-5 text-center text-[#8A7A7A] text-xs">◆</span>
                          <Input value={item.name}
                            onChange={(e) => updateItem(si, ci, ii, { name: e.target.value })}
                            placeholder="Item name" className="flex-1" data-testid={`menu-item-name-${si}-${ci}-${ii}`} />
                          <button onClick={() => updateItem(si, ci, ii, { hidden: !item.hidden })}
                            className={`p-1 rounded ${item.hidden ? 'text-[#8A7A7A] bg-[#F8F5EF]' : 'text-[#3B2F2F]'} hover:bg-[#F8F5EF]`}
                            title={item.hidden ? 'Hidden' : 'Visible'}
                            data-testid={`menu-item-hide-${si}-${ci}-${ii}`}>
                            {item.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => moveItem(si, ci, ii, -1)} disabled={ii === 0}
                            className="p-1 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-item-up-${si}-${ci}-${ii}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => moveItem(si, ci, ii, 1)} disabled={ii === cat.items.length - 1}
                            className="p-1 rounded hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`menu-item-down-${si}-${ci}-${ii}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removeItem(si, ci, ii)}
                            className="p-1 rounded text-red-500 hover:bg-red-50" data-testid={`menu-item-delete-${si}-${ci}-${ii}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => addItem(si, ci)}
                      className="mt-2 text-xs text-[#3B2F2F] inline-flex items-center gap-1 hover:text-[#C8A951]"
                      data-testid={`menu-add-item-${si}-${ci}`}>
                      <Plus className="w-3.5 h-3.5" /> Add item
                    </button>
                  </div>
                ))}
                <button onClick={() => addCategory(si)}
                  className="text-xs text-[#3B2F2F] inline-flex items-center gap-1 hover:text-[#C8A951]"
                  data-testid={`menu-add-category-${si}`}>
                  <Plus className="w-3.5 h-3.5" /> Add category
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button onClick={addSection}
        className="w-full py-3 rounded-xl border border-dashed border-[#C8A951] text-[#3B2F2F] hover:bg-[#F8F5EF] inline-flex items-center justify-center gap-2"
        data-testid="menu-add-section-btn">
        <Plus className="w-4 h-4" /> Add section
      </button>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset menu to defaults?</DialogTitle>
            <DialogDescription>
              This restores the original Happy Hour + Dinner menu you seeded. Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button onClick={resetDefaults} disabled={resetting} className="bg-[#C8A951] hover:bg-[#B09240] text-white">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMenuManager;
